'use client';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { observer } from 'mobx-react-lite';
import { ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import type { VoidCallback } from '@/type-utils';
import type IStylable from '../../../../traits/IStylable';
import { Curtain } from '../../../Curtain';
import useConditionallyDisableScrolling from '../../../hooks/useConditionallyDisableScrolling';

import S from './styles.base.module.scss';

export interface IModalProps extends IStylable {
  /** Called when the close button is clicked. */
  onClose: () => void;

  /** Called when the close animation is finished. */
  onCloseFinished?: VoidCallback;

  /** Called when the open animation is finished. */
  onOpenFinished?: VoidCallback;

  /** Whether the component is currently open. */
  isOpen: boolean;

  /** The contents of the modal. */
  children: ReactNode;

  /** Whether or not the modal is a pane style modal. */
  isPane?: boolean;

  /** The sliding direction of a pane style modal.
   * Default is right.
   * */
  panePosition?: 'right' | 'left';

  /** Whether the modal should take up the full screen. */
  isFullScreen?: boolean;

  /** Whether to hide the dark curtain backdrop. When hidden, the modal can only be closed via explicit close actions (e.g. close button). */
  hideCurtain?: boolean;
}

/**
 * A client-only implementation of the Modal component.
 * It can only be rendered on the client side because it relies on the `document` object.
 *
 * This component have some similarities with `<Popover />` component, but it behaves
 * differently in terms of positioning and animation, therefore it is implemented
 * as a separate component for better separation of concerns.
 *
 * If you need to display content in the screen blocking the screen behind it,
 * you can follow the below guidelines:
 *
 * - Choose `<Modal />` when:
 *   - You want to display a modal dialog appears at the center of the screen.
 *   - You want to display a modal dialog that covers the entire screen.
 *   - You want to display a panel that is fixed to the viewport left or right.
 * - Choose `<Popover />` when:
 *   - You want to display a popover that appears **anchored** to an element.
 *     - Relative can be left, right, top or bottom.
 */
export const ClientOnlyModal = observer(function Modal({
  className,
  style,
  isOpen,
  onClose,
  isPane = false,
  panePosition = 'right',
  children,
  onCloseFinished,
  onOpenFinished,
  isFullScreen = false,
  hideCurtain = false
}: IModalProps) {
  useConditionallyDisableScrolling(isOpen);

  const nodeRef = useRef(null);

  // The style class for the modal. This will determine the type of modal to render, 'modal' or 'pane'.
  // Default is 'modal'.
  const modalClass = !isPane ? 'modal' : 'pane';
  const modalTransitionTimeout = 350;

  return createPortal(
    /**
     * The `CSSTransition` component manages the state of the animated transitions via CSS classes.
     * @see http://reactcommunity.org/react-transition-group/css-transition
     */
    <CSSTransition
      nodeRef={nodeRef}
      appear={isOpen} // Transition on initial render.
      in={isOpen}
      timeout={modalTransitionTimeout}
      mountOnEnter
      /**
       * React batching issues are causing the animation entering not to be properly applied when the element is created via portal.
       *
       * That likely happens due to batching issues with the nodeRef when trying to update the status
       * Adding `unmountOnExit` properly adds the `UNMOUNTED` state to the element, changing the render element to `null` when the element is removed from the DOM
       * and properly triggering the enter animation due to a state refresh.
       *
       * @see {@link https://github.com/reactjs/react-transition-group/blob/master/src/Transition.js | Transition.js}
       * @see {@link https://github.com/reactjs/react-transition-group/issues/932 | Open Issue on GitHub}
       */
      unmountOnExit
      onExited={onCloseFinished}
      onEntered={onOpenFinished}
      classNames={{
        // States for when the modal is mounted to the DOM.
        appear: S[`${modalClass}-enter`],
        appearActive: S[`${modalClass}-enter-active`],
        appearDone: S[`${modalClass}-enter-done`],
        // States for when the modal being shown
        enter: S[`${modalClass}-enter`],
        enterActive: S[`${modalClass}-enter-active`],
        enterDone: S[`${modalClass}-enter-done`],
        // States for when the modal is being hidden and about to be removed from the DOM.
        exit: S[`${modalClass}-exit`],
        exitActive: S[`${modalClass}-exit-active`],
        exitDone: S[`${modalClass}-exit-done`]
      }}
    >
      {/**
       * The "modal" div represents the wrapper for the entire modal dialog box.
       * It's purpose is to contain the CSS transition state classes as passed by the
       * `CSSTransition` parent component.
       */}
      <div ref={nodeRef} className={S[`${modalClass}-enter`]}>
        {/**
         * The "container" div represents the wrapper for the entire viewport which
         * houses the modal dialog box.
         */}
        <div
          className={classes(S.container, isPane && S[`pane-${panePosition}`])}
          role="dialog"
          aria-modal="true"
        >
          {!hideCurtain && (
            <Curtain onClose={onClose} />
          )}
          {/** The "content" div represents the modal dialog box itself. */}
          <div
            className={classes(
              S.content,
              isPane && S.paneContent,
              isFullScreen && S.fullScreen,
              className
            )}
            style={style}
            tabIndex={-1}
          >
            {children}
          </div>
        </div>
      </div>
    </CSSTransition>,
    document.body
  );
});
